import React, { useEffect } from 'react';
import { Container, Box, Typography, Paper, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const PrivacyPolicyPage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const buildSection = (title: string, content: React.ReactNode) => (
    <Box sx={{ mb: 4 }}>
      <Typography 
        variant="h5" 
        fontWeight="bold" 
        color="#673AB7" 
        gutterBottom 
        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' } }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body1" 
        component="div"
        sx={{ lineHeight: 1.8, color: '#424242' }}
      >
        {content}
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography 
        variant="h3" 
        fontWeight="bold" 
        gutterBottom 
        sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
      >
        {t('privacy_policy_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 3 }}>
        {t('privacy_last_updated')}
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
        {t('privacy_intro_commitment')}
      </Typography>

      <Box sx={{ mb: 3, pl: 3 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>• {t('privacy_intro_website')}</Typography>
        <Typography variant="body1">• {t('privacy_intro_mobile')}</Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
        {t('privacy_intro_agreement')}
      </Typography>

      {buildSection(t('privacy_about_title'), 
        <>
          <Typography variant="body1" paragraph>
            {t('privacy_about_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('privacy_about_form_guidance')}</Typography>
            <Typography variant="body1">• {t('privacy_about_scheme_info')}</Typography>
            <Typography variant="body1">• {t('privacy_about_doc_checklists')}</Typography>
          </Box>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: '#FFF3E0',
              border: '2px solid #FFB74D',
              mt: 2
            }}
          >
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              {t('privacy_disclaimer_heading')}
            </Typography>
            <Typography variant="body1" paragraph>
              • {t('privacy_disclaimer_not_affiliated')}
            </Typography>
            <Typography variant="body1" paragraph>
              • {t('privacy_disclaimer_no_submit')}
            </Typography>
            <Typography variant="body1">
              • {t('privacy_disclaimer_info_only')}
            </Typography>
          </Paper>
        </>
      )}

      {buildSection(t('privacy_collect_v2_title'),
        <>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            {t('privacy_collect_website_title')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('privacy_collect_website_intro')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('privacy_collect_website_stored')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1" paragraph>
              • {t('privacy_collect_lang_pref')}
            </Typography>
            <Typography variant="body1">
              • {t('privacy_collect_doc_checklist')}
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            {t('privacy_collect_data_properties')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('privacy_collect_remains_device')}</Typography>
            <Typography variant="body1">• {t('privacy_collect_not_transmitted')}</Typography>
            <Typography variant="body1">• {t('privacy_collect_not_tracking')}</Typography>
          </Box>
          <Typography variant="body1" paragraph>
            {t('privacy_collect_firebase_use')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('privacy_collect_no_analytics')}
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            {t('privacy_collect_mobile_title')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('privacy_collect_mobile_intro')}
          </Typography>

          <Typography variant="body1" fontWeight="bold" gutterBottom>
            {t('privacy_collect_analytics_title')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('privacy_collect_analytics_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('privacy_collect_app_interactions')}</Typography>
            <Typography variant="body1">• {t('privacy_collect_screens_viewed')}</Typography>
            <Typography variant="body1">• {t('privacy_collect_feature_usage')}</Typography>
            <Typography variant="body1">• {t('privacy_collect_device_info')}</Typography>
          </Box>
          <Typography variant="body1" paragraph>
            {t('privacy_collect_analytics_purpose')}
          </Typography>

          <Typography variant="body1" fontWeight="bold" gutterBottom>
            {t('privacy_collect_crash_title')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('privacy_collect_crash_text')}
          </Typography>

          <Typography variant="body1" fontWeight="bold" gutterBottom>
            {t('privacy_collect_local_pref_title')}
          </Typography>
          <Typography variant="body1">
            {t('privacy_collect_local_pref_text')}
          </Typography>
        </>
      )}

      {buildSection(t('privacy_not_collect_v2_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('privacy_not_collect_intro')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('privacy_not_collect_statement')}
          </Typography>
          <Box sx={{ pl: 3 }}>
            <Typography variant="body1">• {t('privacy_not_collect_name')}</Typography>
            <Typography variant="body1">• {t('privacy_not_collect_email')}</Typography>
            <Typography variant="body1">• {t('privacy_not_collect_phone')}</Typography>
            <Typography variant="body1">• {t('privacy_not_collect_govt_id')}</Typography>
            <Typography variant="body1">• {t('privacy_not_collect_financial')}</Typography>
            <Typography variant="body1">• {t('privacy_not_collect_location')}</Typography>
            <Typography variant="body1">• {t('privacy_not_collect_access')}</Typography>
          </Box>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t('privacy_not_sell')}
          </Typography>
        </>
      )}

      {buildSection(t('privacy_use_v2_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('privacy_use_intro')}
          </Typography>
          <Box sx={{ pl: 3 }}>
            <Typography variant="body1">• {t('privacy_use_improve')}</Typography>
            <Typography variant="body1">• {t('privacy_use_analyze')}</Typography>
            <Typography variant="body1">• {t('privacy_use_fix_crashes')}</Typography>
            <Typography variant="body1">• {t('privacy_use_maintain')}</Typography>
            <Typography variant="body1">• {t('privacy_use_remember')}</Typography>
          </Box>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t('privacy_use_no_ads')}
          </Typography>
        </>
      )}

      {buildSection(t('privacy_third_party_v2_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('privacy_third_party_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('privacy_third_party_firebase_analytics')}</Typography>
            <Typography variant="body1">• {t('privacy_third_party_crashlytics')}</Typography>
            <Typography variant="body1">• {t('privacy_third_party_firestore')}</Typography>
            <Typography variant="body1">• {t('privacy_third_party_storage')}</Typography>
          </Box>
          <Typography variant="body1" paragraph>
            {t('privacy_third_party_process')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">
              {t('privacy_third_party_google_policy')}{' '}
              <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">
                https://policies.google.com/privacy
              </Link>
            </Typography>
            <Typography variant="body1">
              {t('privacy_third_party_firebase_terms')}{' '}
              <Link href="https://firebase.google.com/terms" target="_blank" rel="noopener">
                https://firebase.google.com/terms
              </Link>
            </Typography>
          </Box>
          <Typography variant="body1">
            {t('privacy_third_party_review')}
          </Typography>
        </>
      )}

      {buildSection(t('privacy_security_v2_title'),
        <>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('privacy_security_website_prefs')}</Typography>
            <Typography variant="body1">• {t('privacy_security_app_prefs')}</Typography>
            <Typography variant="body1">• {t('privacy_security_https')}</Typography>
            <Typography variant="body1">• {t('privacy_security_safeguards')}</Typography>
          </Box>
          <Typography variant="body1">
            {t('privacy_security_no_guarantee')}
          </Typography>
        </>
      )}

      {buildSection(t('privacy_children_v2_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('privacy_children_general')}
          </Typography>
          <Typography variant="body1">
            {t('privacy_children_under13')}
          </Typography>
        </>
      )}

      {buildSection(t('privacy_rights_v2_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('privacy_rights_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('privacy_rights_clear_browser')}</Typography>
            <Typography variant="body1">• {t('privacy_rights_clear_app')}</Typography>
            <Typography variant="body1">• {t('privacy_rights_uninstall')}</Typography>
            <Typography variant="body1">• {t('privacy_rights_contact')}</Typography>
          </Box>
          <Typography variant="body1">
            {t('privacy_rights_no_accounts')}
          </Typography>
        </>
      )}

      {buildSection(t('privacy_changes_v2_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('privacy_changes_update')}
          </Typography>
          <Typography variant="body1">
            {t('privacy_changes_acceptance')}
          </Typography>
        </>
      )}

      {buildSection(t('privacy_contact_v2_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('privacy_contact_intro')}
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>{t('privacy_contact_name')}</strong><br />
            {t('privacy_contact_email')}
          </Typography>
          <Typography variant="body1">
            {t('privacy_contact_response')}
          </Typography>
        </>
      )}
    </Container>
  );
};
